import { del, getBodyOnly, getApiErrorMessage, patch, post, put } from "./aws_api_helper";
import * as url from "./url_helper";
import { AUDIT_HUB_USE_MOCK } from "../pages/Compliance/AuditHub/constants";
import * as mock from "../pages/Compliance/AuditHub/api/mock";

const auditsBase = url.AUDIT_HUB_AUDITS;
const catalogBase = url.AUDIT_HUB_CATALOG;

const isHttpError = (res) =>
  res && typeof res.status === "number" && res.status >= 400;

const toError = (res) => {
  const err = new Error(getApiErrorMessage(res) || "Request failed.");
  err.status = res?.status;
  err.data = res?.data ?? res;
  return err;
};

const unwrapGet = (res) => {
  if (isHttpError(res)) throw toError(res);
  return res;
};

const unwrapPost = (res) => {
  if (isHttpError(res)) throw toError(res);
  if (res && res.data !== undefined && typeof res.status === "number") {
    return res.data;
  }
  return res;
};

/** `del` returns the body as a JSON string. */
const unwrapDelete = (res) => {
  const body = unwrapPost(res);
  if (typeof body !== "string") return body;
  try {
    return body ? JSON.parse(body) : {};
  } catch (_error) {
    return {};
  }
};

const withQuery = (path, params) => {
  if (!params) return path;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

export const listAudits = (params = {}) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockListAudits(params)
    : getBodyOnly(withQuery(auditsBase, params));

export const createAudit = (body) =>
  AUDIT_HUB_USE_MOCK ? mock.mockCreateAudit(body) : post(auditsBase, body);

export const addAuditAccount = (auditId, body) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockAddAccount(auditId, body)
    : post(`${auditsBase}/${encodeURIComponent(auditId)}/accounts`, body);

export const addAuditFrameworks = (auditId, auditAccountId, complianceAddonIds) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/accounts/${encodeURIComponent(auditAccountId)}/frameworks`,
    { complianceAddonIds }
  );

export const removeAuditAccount = (auditId, auditAccountId, { force = false } = {}) =>
  del(
    withQuery(`${auditsBase}/${encodeURIComponent(auditId)}/accounts/${encodeURIComponent(auditAccountId)}`, {
      force: force || undefined,
    })
  );

export const removeAuditFramework = (auditId, auditAccountId, complianceAddonId, { force = false } = {}) =>
  del(
    withQuery(
      `${auditsBase}/${encodeURIComponent(auditId)}/accounts/${encodeURIComponent(auditAccountId)}/frameworks/${encodeURIComponent(complianceAddonId)}`,
      { force: force || undefined }
    )
  );

export const rescanAudit = (auditId, auditAccountId) =>
  post(withQuery(`${auditsBase}/${encodeURIComponent(auditId)}/scan`, { auditAccountId }));

export const launchAudit = (auditId) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockLaunchAudit(auditId)
    : post(`${auditsBase}/${encodeURIComponent(auditId)}/launch`);

export const deleteDraftAudit = (auditId) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockDeleteDraft(auditId)
    : del(`${auditsBase}/${encodeURIComponent(auditId)}`);

export const getAudit = (auditId) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockGetAudit(auditId)
    : getBodyOnly(`${auditsBase}/${encodeURIComponent(auditId)}`);

export const updateAudit = (auditId, body) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockUpdateAudit(auditId, body)
    : patch(`${auditsBase}/${encodeURIComponent(auditId)}`, body);

export const setAuditAuditors = (auditId, auditorEmails) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockSetAuditors(auditId, { auditorEmails })
    : put(`${auditsBase}/${encodeURIComponent(auditId)}/auditors`, { auditorEmails });

export const completeAudit = (auditId, body = {}) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockCompleteAudit(auditId, body)
    : post(`${auditsBase}/${encodeURIComponent(auditId)}/complete`, body);

export const cancelAudit = (auditId, body) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockCancelAudit(auditId, body)
    : post(`${auditsBase}/${encodeURIComponent(auditId)}/cancel`, body);

export const listAuditControls = (auditId, params = {}) =>
  getBodyOnly(withQuery(`${auditsBase}/${encodeURIComponent(auditId)}/controls`, params));

export const getAuditControl = (auditId, auditControlId) =>
  getBodyOnly(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}`
  );

export const updateAuditControl = (auditId, auditControlId, body) =>
  patch(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}`,
    body
  );

export const transitionAuditControl = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/transition`,
    body
  );

export const answerAuditCheck = (auditId, auditControlId, checkId, body) =>
  put(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/checks/${encodeURIComponent(checkId)}`,
    body
  );

export const applyControlToScopes = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/apply-to-scopes`,
    body
  );

export const createEvidenceUploadUrl = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/evidence/upload-url`,
    body
  );

export const completeEvidenceUpload = (auditId, auditControlId, evidenceId) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/evidence/${encodeURIComponent(evidenceId)}/complete`,
    {}
  );

export const addEvidenceLink = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/evidence/link`,
    body
  );

export const listAuditEvidence = (auditId, auditControlId, params = {}) =>
  getBodyOnly(
    withQuery(
      `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/evidence`,
      params
    )
  );

export const getEvidenceDownloadUrl = (auditId, evidenceId) =>
  getBodyOnly(
    `${auditsBase}/${encodeURIComponent(auditId)}/evidence/${encodeURIComponent(evidenceId)}/download-url`
  );

export const deleteEvidence = (auditId, evidenceId) =>
  del(`${auditsBase}/${encodeURIComponent(auditId)}/evidence/${encodeURIComponent(evidenceId)}`);

export const listAuditComments = (auditId, auditControlId, params = {}) =>
  getBodyOnly(
    withQuery(
      `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/comments`,
      params
    )
  );

export const createAuditComment = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/comments`,
    body
  );

export const deleteAuditComment = (auditId, commentId) =>
  del(`${auditsBase}/${encodeURIComponent(auditId)}/comments/${encodeURIComponent(commentId)}`);

export const listAuditTasks = (auditId, auditControlId, params = {}) =>
  getBodyOnly(
    withQuery(
      `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/tasks`,
      params
    )
  );

export const createAuditTask = (auditId, auditControlId, body) =>
  post(
    `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/tasks`,
    body
  );

export const updateAuditTask = (auditId, taskId, body) =>
  patch(`${auditsBase}/${encodeURIComponent(auditId)}/tasks/${encodeURIComponent(taskId)}`, body);

export const listAuditEvents = (auditId, params = {}) =>
  getBodyOnly(withQuery(`${auditsBase}/${encodeURIComponent(auditId)}/events`, params));

export const listAuditControlEvents = (auditId, auditControlId, params = {}) =>
  getBodyOnly(
    withQuery(
      `${auditsBase}/${encodeURIComponent(auditId)}/controls/${encodeURIComponent(auditControlId)}/events`,
      params
    )
  );

export const requestAuditReport = (auditId, body = { format: "docx" }) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockRequestReport(auditId, body)
    : post(`${auditsBase}/${encodeURIComponent(auditId)}/report`, body);

export const getAuditJob = (auditId, jobId) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockGetJob(auditId, jobId)
    : getBodyOnly(`${auditsBase}/${encodeURIComponent(auditId)}/jobs/${encodeURIComponent(jobId)}`);

export const listCatalogFrameworks = (params = {}) =>
  AUDIT_HUB_USE_MOCK
    ? mock.mockListFrameworks(params)
    : getBodyOnly(withQuery(`${catalogBase}/frameworks`, params));

/** Follows `page` until `total` is loaded. Catalog size defaults to 100; one short page drops frameworks from the create form. */
export const listAllCatalogFrameworks = async (params = {}) => {
  const size = 100;
  const first = unwrapGet(await listCatalogFrameworks({ ...params, page: 1, size }));
  const items = [...(first.items || [])];
  const total = Number(first.total);
  const pageSize = Number(first.size) || size;
  let page = Number(first.page) || 1;
  const pageCount = Number.isFinite(total) ? Math.ceil(total / pageSize) : 1;
  while (page < pageCount && page < 10) {
    page += 1;
    const next = unwrapGet(await listCatalogFrameworks({ ...params, page, size: pageSize }));
    const batch = next.items || [];
    if (!batch.length) break;
    items.push(...batch);
  }
  return items;
};

/** Reads every page of a `{ page, size, total, items }` list; audit-scoped lists cap `size` at 100. */
export const collectPages = async (fetchPage, { size = 100, maxPages = 50 } = {}) => {
  const items = [];
  let page = 1;
  let total = Infinity;
  while (items.length < total && page <= maxPages) {
    const body = unwrapGet(await fetchPage({ page, size }));
    const batch = Array.isArray(body?.items) ? body.items : [];
    items.push(...batch);
    total = Number(body?.total);
    if (!Number.isFinite(total) || !batch.length) break;
    page += 1;
  }
  return items;
};

export const listManualChecks = (params) =>
  getBodyOnly(withQuery(`${catalogBase}/manual-checks`, params));

export const getCatalogCoverage = (complianceKey) =>
  getBodyOnly(withQuery(`${catalogBase}/coverage`, { complianceKey }));

export { unwrapDelete, unwrapGet, unwrapPost };
